import {
  useCallback,
  useMemo,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {setPreviewFrontFace, clearPreviewFrontFace} from '../../../common/components/deviceMesh/previewFrontFace'
import {
  setPreviewFrontSpeaker,
  clearPreviewFrontSpeaker,
} from '../../../common/components/deviceMesh/previewFrontSpeaker'
import {setPreviewHomeButton, clearPreviewHomeButton} from '../../../common/components/deviceMesh/previewHomeButton'
import {
  setPreviewDecalFeature,
  clearPreviewDecalFeature,
} from '../../../common/components/deviceMesh/previewDecalFeatures'
import type {PreviewDecalFeature} from '../../../common/components/deviceMesh/previewDecalFeatures'
import {setRemovedFrontFeature} from '../../../common/components/deviceMesh/removedFrontFeatureStore'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Switch,
  Button,
  Menu,
  MenuItem,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CropSquareIcon from '@mui/icons-material/CropSquare'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setFrontFaceInset,
  setDisplayCornerRadius,
  setDisplayInsetX,
  setDisplayInsetY,
  setDisplayReflection,
  addFrontFaceFeature,
  removeFrontFaceFeature,
  updateFrontFaceFeature,
  addCustomDecal,
  removeCustomDecal,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import OffsetPad from '../sharedWidgets/OffsetPad'
import CustomDecalItem from './CustomDecalItem'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {
  FrontFaceFeatureConfig,
  FrontFaceFeatureType,
  HomeButtonFeature,
  CameraLensFeature,
  DisplayCutoutFeature,
  HomeIndicatorFeature,
  FrontSpeakerFeature,
} from '../../../common/components/types'
import SliderRow from '../sharedWidgets/SliderRow'
import {FRONT_FACE_GAP} from '../../../common/components/workerGeo/helpers'
import {generateCustomDecalId} from '../../../common/types/customDecalTypes'

// ── Slider range limits ───────────────────────────────────────────

const RANGES = {
  frontFaceInset: {min: 0, max: 15, step: 0.1},
  displayCornerRadius: {min: 0, max: 20, step: 0.1},
  displayInset: {min: 0, max: 30, step: 0.1},
  homeButtonSize: {min: 1, max: 15, step: 0.1},
  cameraLensRadius: {min: 0.5, max: 5, step: 0.1},
  cutoutWidth: {min: 1, max: 40, step: 0.1},
  cutoutHeight: {min: 0.5, max: 10, step: 0.1},
  indicatorWidth: {min: 5, max: 40, step: 0.1},
  indicatorHeight: {min: 0.3, max: 5, step: 0.1},
  speakerWidth: {min: 0.5, step: 0.1},
  speakerHeight: {min: 0.1, step: 0.05},
  displayReflection: {min: 0, max: 100, step: 1},
} as const

// ── Feature type list for the add menu ────────────────────────────

const FEATURE_TYPES: FrontFaceFeatureType[] = [
  'homeButton',
  'cameraLens',
  'displayCutout',
  'homeIndicator',
  'frontSpeaker',
]

// ── Default values for new features ───────────────────────────────

interface FeatureDefaultsMap {
  homeButton: Omit<HomeButtonFeature, 'id'>
  cameraLens: Omit<CameraLensFeature, 'id'>
  displayCutout: Omit<DisplayCutoutFeature, 'id'>
  homeIndicator: Omit<HomeIndicatorFeature, 'id'>
  frontSpeaker: Omit<FrontSpeakerFeature, 'id'>
}

const FEATURE_DEFAULTS: FeatureDefaultsMap = {
  homeButton: {type: 'homeButton', radius: 5.5, x: 0, y: -55},
  cameraLens: {type: 'cameraLens', radius: 1.4, xOffset: 7, yOffset: 66},
  displayCutout: {
    type: 'displayCutout',
    width: 20,
    height: 5.2,
    cornerRadius: 4.5,
    xOffset: 0,
    yOffset: 66,
  },
  homeIndicator: {
    type: 'homeIndicator',
    width: 25,
    height: 1,
    cornerRadius: 0.6,
    yOffset: -68,
    dark: false,
  },
  frontSpeaker: {type: 'frontSpeaker', width: 10, height: 0.3, cornerRadius: 0.8, xOffset: 0, yOffset: 71.4},
}

// ── Feature controls per type ─────────────────────────────────────

interface FeatureControlsProps {
  feature: FrontFaceFeatureConfig
  unit: string
  deviceWidth: number
  deviceHeight: number
  deviceEdgeBevel: number
  frontFaceInset: number
  /** Front face panel area width (accounts for bevel, inset, gap) */
  frontFaceWidth: number
  /** Front face panel area height (accounts for bevel, inset, gap) */
  frontFaceHeight: number
  onUpdate: (changes: Partial<FrontFaceFeatureConfig>) => void
  /** Live preview callback for front speaker (writes to preview store, no Redux) */
  onSpeakerPreview?: (changes: Partial<FrontSpeakerFeature>) => void
  /** Committed callback for front speaker (clears preview + dispatches to Redux) */
  onSpeakerCommit?: (changes: Partial<FrontSpeakerFeature>) => void
  /** Live preview callback for home button (writes to preview store, no Redux) */
  onHomeButtonPreview?: (changes: Partial<HomeButtonFeature>) => void
  /** Committed callback for home button (clears preview + dispatches to Redux) */
  onHomeButtonCommit?: (changes: Partial<HomeButtonFeature>) => void
  /** Live preview callback for decal features (writes to preview store, no Redux) */
  onDecalPreview?: (changes: Partial<PreviewDecalFeature>) => void
  /** Committed callback for decal features (clears preview + dispatches to Redux) */
  onDecalCommit?: (changes: Partial<PreviewDecalFeature>) => void
}

const FeatureControls = ({
  feature,
  unit,
  deviceWidth,
  deviceHeight,
  deviceEdgeBevel,
  frontFaceInset,
  frontFaceWidth,
  frontFaceHeight,
  onUpdate,
  onSpeakerPreview,
  onSpeakerCommit,
  onHomeButtonPreview,
  onHomeButtonCommit,
  onDecalPreview,
  onDecalCommit,
}: FeatureControlsProps) => {
  const {t} = useTranslation()

  const maxW = Math.round((deviceWidth - 2 * deviceEdgeBevel - 2 * frontFaceInset) * 10) / 10
  const maxH = Math.round((deviceHeight - 2 * deviceEdgeBevel - 2 * frontFaceInset) * 10) / 10

  switch (feature.type) {
    case 'homeButton': {
      // Dynamic limits — button can reach the full edge of the body's flat front
      const r = feature.radius
      const maxR = Math.round(Math.min(RANGES.homeButtonSize.max, frontFaceWidth / 2, frontFaceHeight / 2) * 10) / 10
      const limX = Math.round(Math.max(0, deviceWidth / 2 - deviceEdgeBevel - r) * 10) / 10
      const limY = Math.round(Math.max(0, deviceHeight / 2 - deviceEdgeBevel - r) * 10) / 10
      return (
        <>
          <OffsetPad
            valueX={feature.x}
            valueY={-feature.y}
            min={-limY}
            max={limY}
            minX={-limX}
            maxX={limX}
            step={0.1}
            onChangeX={(v: number) => onHomeButtonPreview?.({x: v})}
            onChangeY={(v: number) => onHomeButtonPreview?.({y: -v})}
            onCommitX={onHomeButtonCommit ? (v: number) => onHomeButtonCommit({x: v}) : undefined}
            onCommitY={onHomeButtonCommit ? (v: number) => onHomeButtonCommit({y: -v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.homeButtonRadius')}
            value={feature.radius}
            min={RANGES.homeButtonSize.min}
            max={maxR}
            step={RANGES.homeButtonSize.step}
            unit={unit}
            onChange={(v: number) => onHomeButtonPreview?.({radius: v})}
            onChangeCommitted={onHomeButtonCommit ? (v: number) => onHomeButtonCommit({radius: v}) : undefined}
          />
        </>
      )
    }
    case 'cameraLens': {
      const lensLimX = Math.round((deviceWidth / 2 - deviceEdgeBevel - feature.radius) * 10) / 10
      const lensLimY = Math.round((deviceHeight / 2 - deviceEdgeBevel - feature.radius) * 10) / 10
      return (
        <>
          <OffsetPad
            valueX={feature.xOffset}
            valueY={-feature.yOffset}
            min={-lensLimY}
            max={lensLimY}
            minX={-lensLimX}
            maxX={lensLimX}
            step={0.1}
            onChangeX={(v: number) => onDecalPreview?.({xOffset: v})}
            onChangeY={(v: number) => onDecalPreview?.({yOffset: -v})}
            onCommitX={onDecalCommit ? (v: number) => onDecalCommit({xOffset: v}) : undefined}
            onCommitY={onDecalCommit ? (v: number) => onDecalCommit({yOffset: -v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.cameraLensRadius')}
            value={feature.radius}
            {...RANGES.cameraLensRadius}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({radius: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({radius: v}) : undefined}
          />
        </>
      )
    }
    case 'displayCutout': {
      const cutoutLimX = Math.round((deviceWidth / 2 - deviceEdgeBevel - feature.width / 2) * 10) / 10
      const cutoutLimY = Math.round((deviceHeight / 2 - deviceEdgeBevel - feature.height / 2) * 10) / 10
      return (
        <>
          <OffsetPad
            valueX={feature.xOffset}
            valueY={-feature.yOffset}
            min={-cutoutLimY}
            max={cutoutLimY}
            minX={-cutoutLimX}
            maxX={cutoutLimX}
            step={0.1}
            onChangeX={(v: number) => onDecalPreview?.({xOffset: v})}
            onChangeY={(v: number) => onDecalPreview?.({yOffset: -v})}
            onCommitX={onDecalCommit ? (v: number) => onDecalCommit({xOffset: v}) : undefined}
            onCommitY={onDecalCommit ? (v: number) => onDecalCommit({yOffset: -v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.displayCutoutWidth')}
            value={feature.width}
            min={1}
            max={maxW}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({width: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({width: v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.displayCutoutHeight')}
            value={feature.height}
            min={0.5}
            max={maxH}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({height: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({height: v}) : undefined}
          />
        </>
      )
    }
    case 'homeIndicator': {
      const indicatorLimY = Math.round((deviceHeight / 2 - deviceEdgeBevel - feature.height / 2) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.frontFace.homeIndicatorY')}
            value={feature.yOffset}
            min={-indicatorLimY}
            max={indicatorLimY}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({yOffset: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({yOffset: v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.homeIndicatorWidth')}
            value={feature.width}
            min={1}
            max={maxW}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({width: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({width: v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.homeIndicatorHeight')}
            value={feature.height}
            min={0.3}
            max={maxH}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onDecalPreview?.({height: v})}
            onChangeCommitted={onDecalCommit ? (v: number) => onDecalCommit({height: v}) : undefined}
          />
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '4px'}}>
            <Typography sx={{fontSize: 11, fontWeight: 600}}>{t('device.frontFace.homeIndicatorDark')}</Typography>
            <Switch
              size="small"
              checked={feature.dark}
              onChange={(_: ChangeEvent<HTMLInputElement>, checked: boolean) => onUpdate({dark: checked})}
            />
          </Box>
        </>
      )
    }
    case 'frontSpeaker': {
      // Loosened limits — speaker can be placed anywhere on the body's flat front
      // (X/Y position pad) and sized up to the full flat front, like side-edge speakers.
      const spkMaxW = Math.round(Math.max(RANGES.speakerWidth.min, deviceWidth - 2 * deviceEdgeBevel) * 10) / 10
      const spkMaxH = Math.round(Math.max(RANGES.speakerHeight.min, deviceHeight - 2 * deviceEdgeBevel) * 10) / 10
      const spkLimX = Math.round(Math.max(0, deviceWidth / 2 - deviceEdgeBevel - feature.width / 2) * 10) / 10
      const spkLimY = Math.round(Math.max(0, deviceHeight / 2 - deviceEdgeBevel - feature.height / 2) * 10) / 10
      return (
        <>
          <OffsetPad
            valueX={feature.xOffset}
            valueY={-feature.yOffset}
            min={-spkLimY}
            max={spkLimY}
            minX={-spkLimX}
            maxX={spkLimX}
            step={0.1}
            onChangeX={(v: number) => onSpeakerPreview?.({xOffset: v})}
            onChangeY={(v: number) => onSpeakerPreview?.({yOffset: -v})}
            onCommitX={onSpeakerCommit ? (v: number) => onSpeakerCommit({xOffset: v}) : undefined}
            onCommitY={onSpeakerCommit ? (v: number) => onSpeakerCommit({yOffset: -v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.frontSpeakerWidth')}
            value={feature.width}
            min={RANGES.speakerWidth.min}
            max={spkMaxW}
            step={RANGES.speakerWidth.step}
            unit={unit}
            onChange={(v: number) => onSpeakerPreview?.({width: v})}
            onChangeCommitted={onSpeakerCommit ? (v: number) => onSpeakerCommit({width: v}) : undefined}
          />
          <SliderRow
            label={t('device.frontFace.frontSpeakerHeight')}
            value={feature.height}
            min={RANGES.speakerHeight.min}
            max={spkMaxH}
            step={RANGES.speakerHeight.step}
            unit={unit}
            onChange={(v: number) => onSpeakerPreview?.({height: v})}
            onChangeCommitted={onSpeakerCommit ? (v: number) => onSpeakerCommit({height: v}) : undefined}
          />
        </>
      )
    }
  }
}

// ── DeviceFrontFacePanel — front face / display shape controls ─────

const DeviceFrontFacePanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const frontFaceInset = useAppSelector((s) => selectActiveScene(s).frontFaceInset)
  const displayCornerRadius = useAppSelector((s) => selectActiveScene(s).displayCornerRadius)
  const displayInsetX = useAppSelector((s) => selectActiveScene(s).displayInsetX)
  const displayInsetY = useAppSelector((s) => selectActiveScene(s).displayInsetY)
  const displayReflection = useAppSelector((s) => selectActiveScene(s).displayReflection)
  const frontFaceFeatures = useAppSelector((s) => selectActiveScene(s).frontFaceFeatures)
  const customDecals = useAppSelector((s) => selectActiveScene(s).customDecals)
  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const deviceEdgeBevel = useAppSelector((s) => selectActiveScene(s).deviceEdgeBevel)
  const deviceCornerRadius = useAppSelector((s) => selectActiveScene(s).deviceCornerRadius)

  const frontDecals = useMemo(() => customDecals.filter((d) => d.placement === 'front'), [customDecals])

  // Compute the front face corner radius (same formula as the worker)
  const ffGap = FRONT_FACE_GAP
  // Front face panel area dimensions (same formula as the worker)
  const {fw: frontFaceWidth, fh: frontFaceHeight} = useMemo(() => {
    const clampedBevel = Math.min(deviceEdgeBevel, deviceWidth / 2, deviceHeight / 2)
    const baseW = deviceWidth - 2 * clampedBevel
    const baseH = deviceHeight - 2 * clampedBevel
    return {
      fw: Math.max(0.01, baseW - 2 * frontFaceInset - 2 * ffGap),
      fh: Math.max(0.01, baseH - 2 * frontFaceInset - 2 * ffGap),
    }
  }, [deviceWidth, deviceHeight, deviceEdgeBevel, frontFaceInset, ffGap])

  const fcr = useMemo(
    () => Math.min(Math.max(0, deviceCornerRadius - frontFaceInset - ffGap), frontFaceWidth / 2, frontFaceHeight / 2),
    [deviceCornerRadius, frontFaceInset, ffGap, frontFaceWidth, frontFaceHeight],
  )

  // ── Dynamic limits: prevent display corners from extending beyond the front face rounded corners.
  // The constraint is a circle-in-circle containment: the display corner arc (radius dcr) must
  // fit inside the front face corner arc (radius fcr). The 2D formula is much less restrictive
  // than a simple linear subtraction.

  // Minimum display inset X — accounts for the 2D arc geometry given insetY and dcr
  const minDisplayInsetX = useMemo(() => {
    const r = fcr - displayCornerRadius // effective front face arc minus display arc
    if (r <= 0) return 0 // display corner radius >= front face corner radius — always fits
    const b = Math.max(0, fcr - displayInsetY - displayCornerRadius)
    if (b <= 0) return 0 // Y-side is in the straight section — no corner constraint
    if (b >= r) return r // edge case: would need full inset
    return Math.max(0, Math.round((r - Math.sqrt(r * r - b * b)) * 10) / 10)
  }, [fcr, displayCornerRadius, displayInsetY])

  // Minimum display inset Y — symmetric to X
  const minDisplayInsetY = useMemo(() => {
    const r = fcr - displayCornerRadius
    if (r <= 0) return 0
    const b = Math.max(0, fcr - displayInsetX - displayCornerRadius)
    if (b <= 0) return 0
    if (b >= r) return r
    return Math.max(0, Math.round((r - Math.sqrt(r * r - b * b)) * 10) / 10)
  }, [fcr, displayCornerRadius, displayInsetX])

  // Minimum display corner radius — derived from the quadratic arc containment constraint
  const minDisplayCornerRadius = useMemo(() => {
    if (fcr <= 0) return 0
    const ix = displayInsetX
    const iy = displayInsetY
    // From the quadratic: dcr >= fcr - ix - iy - sqrt(2*ix*iy)
    const v = fcr - ix - iy - Math.sqrt(2 * ix * iy)
    return Math.max(0, Math.round(v * 10) / 10)
  }, [fcr, displayInsetX, displayInsetY])

  // Live handlers — write to preview store during drag (bypasses Redux/undo)
  const handleFrontFaceInsetChange = useCallback(
    (v: number) =>
      setPreviewFrontFace({
        frontFaceInset: v,
        displayCornerRadius,
        displayInsetX,
        displayInsetY,
      }),
    [displayCornerRadius, displayInsetX, displayInsetY],
  )
  const handleDisplayCornerRadiusChange = useCallback(
    (v: number) =>
      setPreviewFrontFace({
        frontFaceInset,
        displayCornerRadius: v,
        displayInsetX,
        displayInsetY,
      }),
    [frontFaceInset, displayInsetX, displayInsetY],
  )
  const handleDisplayInsetXChange = useCallback(
    (v: number) =>
      setPreviewFrontFace({
        frontFaceInset,
        displayCornerRadius,
        displayInsetX: v,
        displayInsetY,
      }),
    [frontFaceInset, displayCornerRadius, displayInsetY],
  )
  const handleDisplayInsetYChange = useCallback(
    (v: number) =>
      setPreviewFrontFace({
        frontFaceInset,
        displayCornerRadius,
        displayInsetX,
        displayInsetY: v,
      }),
    [frontFaceInset, displayCornerRadius, displayInsetX],
  )

  // Committed handlers — clear preview and dispatch to Redux
  const handleFrontFaceInsetCommit = useCallback(
    (v: number) => {
      clearPreviewFrontFace()
      dispatch(setFrontFaceInset(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleDisplayCornerRadiusCommit = useCallback(
    (v: number) => {
      clearPreviewFrontFace()
      dispatch(setDisplayCornerRadius(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleDisplayInsetXCommit = useCallback(
    (v: number) => {
      clearPreviewFrontFace()
      dispatch(setDisplayInsetX(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleDisplayInsetYCommit = useCallback(
    (v: number) => {
      clearPreviewFrontFace()
      dispatch(setDisplayInsetY(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  // Add feature menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  // Track the most-recently-added feature/decal id so its accordion mounts expanded.
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null)
  const handleOpenMenu = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(e.currentTarget)
  }, [])
  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null)
  }, [])

  const handleAddFeature = useCallback(
    (featureType: FrontFaceFeatureType) => {
      const id = `${featureType}-${Date.now()}`
      const defaults = FEATURE_DEFAULTS[featureType]

      // Clamp default positions to fit within the current front face area
      let clamped: Omit<FrontFaceFeatureConfig, 'id'>
      switch (featureType) {
        case 'homeButton': {
          const d = defaults as typeof FEATURE_DEFAULTS.homeButton
          const r = Math.min(d.radius, frontFaceWidth / 2, frontFaceHeight / 2)
          const limX = Math.max(0, deviceWidth / 2 - deviceEdgeBevel - r)
          const limY = Math.max(0, deviceHeight / 2 - deviceEdgeBevel - r)
          clamped = {
            ...d,
            radius: r,
            x: Math.max(-limX, Math.min(limX, d.x)),
            y: Math.max(-limY, Math.min(limY, d.y)),
          } as Omit<FrontFaceFeatureConfig, 'id'>
          break
        }
        case 'cameraLens': {
          const d = defaults as typeof FEATURE_DEFAULTS.cameraLens
          const r = d.radius
          const limX = Math.max(0, frontFaceWidth / 2 - r)
          const limY = Math.max(0, frontFaceHeight / 2 - r)
          clamped = {
            ...d,
            xOffset: Math.max(-limX, Math.min(limX, d.xOffset)),
            yOffset: Math.max(-limY, Math.min(limY, d.yOffset)),
          } as Omit<FrontFaceFeatureConfig, 'id'>
          break
        }
        case 'displayCutout': {
          const d = defaults as typeof FEATURE_DEFAULTS.displayCutout
          const limX = Math.max(0, frontFaceWidth / 2)
          const limY = Math.max(0, frontFaceHeight / 2)
          clamped = {
            ...d,
            xOffset: Math.max(-limX, Math.min(limX, d.xOffset)),
            yOffset: Math.max(-limY, Math.min(limY, d.yOffset)),
          } as Omit<FrontFaceFeatureConfig, 'id'>
          break
        }
        case 'homeIndicator': {
          const d = defaults as typeof FEATURE_DEFAULTS.homeIndicator
          const limY = Math.max(0, frontFaceHeight / 2)
          clamped = {...d, yOffset: Math.max(-limY, Math.min(limY, d.yOffset))} as Omit<FrontFaceFeatureConfig, 'id'>
          break
        }
        case 'frontSpeaker': {
          const d = defaults as typeof FEATURE_DEFAULTS.frontSpeaker
          const limY = Math.max(0, deviceHeight / 2 - deviceEdgeBevel)
          clamped = {...d, yOffset: Math.max(-limY, Math.min(limY, d.yOffset))} as Omit<FrontFaceFeatureConfig, 'id'>
          break
        }
      }
      dispatch(addFrontFaceFeature({...clamped, id} as FrontFaceFeatureConfig))
      setNewlyAddedId(id)
      setMenuAnchor(null)
    },
    [dispatch, frontFaceWidth, frontFaceHeight, deviceWidth, deviceHeight, deviceEdgeBevel],
  )

  const handleRemoveFeature = useCallback(
    (id: string) => {
      // Capture worker-affecting feature data before removal for the red ghost
      const feature = frontFaceFeatures.find((f) => f.id === id)
      if (feature) {
        if (feature.type === 'homeButton') {
          setRemovedFrontFeature(id, {
            type: 'homeButton',
            radius: feature.radius,
            x: feature.x,
            y: feature.y,
          })
        } else if (feature.type === 'frontSpeaker') {
          setRemovedFrontFeature(id, {
            type: 'frontSpeaker',
            width: feature.width,
            height: feature.height,
            cornerRadius: feature.cornerRadius,
            xOffset: feature.xOffset ?? 0,
            yOffset: feature.yOffset,
          })
        }
      }
      dispatch(removeFrontFaceFeature(id))
    },
    [dispatch, frontFaceFeatures],
  )

  const handleUpdateFeature = useCallback(
    (id: string, changes: Partial<FrontFaceFeatureConfig>) => {
      dispatch(updateFrontFaceFeature({id, changes}))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const handleAddDecal = useCallback(() => {
    const id = generateCustomDecalId()
    dispatch(addCustomDecal({placement: 'front', id}))
    setNewlyAddedId(id)
    setMenuAnchor(null)
  }, [dispatch])

  const handleRemoveDecal = useCallback((id: string) => dispatch(removeCustomDecal(id)), [dispatch])

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const unit = t('device.shapes.unit')

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <CropSquareIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('device.frontFace.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        {/* Front Face & Display group */}
        <Box sx={{...sx.groupCard, padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px'}}>
          <SliderRow
            label={t('device.frontFace.inset')}
            value={frontFaceInset}
            {...RANGES.frontFaceInset}
            unit={unit}
            onChange={handleFrontFaceInsetChange}
            onChangeCommitted={handleFrontFaceInsetCommit}
          />
          <SliderRow
            label={t('device.frontFace.displayCornerRadius')}
            value={Math.max(displayCornerRadius, minDisplayCornerRadius)}
            min={minDisplayCornerRadius}
            max={RANGES.displayCornerRadius.max}
            step={RANGES.displayCornerRadius.step}
            unit={unit}
            onChange={handleDisplayCornerRadiusChange}
            onChangeCommitted={handleDisplayCornerRadiusCommit}
          />
          <SliderRow
            label={t('device.frontFace.displayInsetX')}
            value={Math.max(displayInsetX, minDisplayInsetX)}
            min={minDisplayInsetX}
            max={RANGES.displayInset.max}
            step={RANGES.displayInset.step}
            unit={unit}
            onChange={handleDisplayInsetXChange}
            onChangeCommitted={handleDisplayInsetXCommit}
          />
          <SliderRow
            label={t('device.frontFace.displayInsetY')}
            value={Math.max(displayInsetY, minDisplayInsetY)}
            min={minDisplayInsetY}
            max={RANGES.displayInset.max}
            step={RANGES.displayInset.step}
            unit={unit}
            onChange={handleDisplayInsetYChange}
            onChangeCommitted={handleDisplayInsetYCommit}
          />
          <SliderRow
            label={t('device.frontFace.displayReflection')}
            value={Math.round(displayReflection * 100)}
            {...RANGES.displayReflection}
            unit="%"
            onChange={(v) => dispatch(setDisplayReflection(v / 100))}
            onChangeCommitted={() => dispatch(sealUndo())}
          />
        </Box>

        {/* Feature groups — one accordion per feature inside a grouped card */}
        {frontFaceFeatures.map((feature) => (
          <Box key={feature.id} sx={sx.groupCard}>
            <Accordion disableGutters square defaultExpanded={feature.id === newlyAddedId} sx={sx.featureAccordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.featureExpandIcon} />} sx={sx.featureSummary}>
                <Typography sx={sx.groupLabel}>{t(`device.frontFace.featureTypes.${feature.type}`)}</Typography>
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={(e: ReactMouseEvent) => {
                    e.stopPropagation()
                    handleRemoveFeature(feature.id)
                  }}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      handleRemoveFeature(feature.id)
                    }
                  }}
                  sx={sx.deleteButton}
                >
                  <DeleteIcon sx={{fontSize: 16}} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={sx.featureDetails}>
                <FeatureControls
                  feature={feature}
                  unit={unit}
                  deviceWidth={deviceWidth}
                  deviceHeight={deviceHeight}
                  deviceEdgeBevel={deviceEdgeBevel}
                  frontFaceInset={frontFaceInset}
                  frontFaceWidth={frontFaceWidth}
                  frontFaceHeight={frontFaceHeight}
                  onUpdate={(changes) => handleUpdateFeature(feature.id, changes)}
                  onSpeakerPreview={
                    feature.type === 'frontSpeaker'
                      ? (changes) => {
                          const fs = feature as FrontSpeakerFeature
                          setPreviewFrontSpeaker(feature.id, fs, changes)
                        }
                      : undefined
                  }
                  onSpeakerCommit={
                    feature.type === 'frontSpeaker'
                      ? (changes) => {
                          clearPreviewFrontSpeaker()
                          handleUpdateFeature(feature.id, changes)
                        }
                      : undefined
                  }
                  onHomeButtonPreview={
                    feature.type === 'homeButton'
                      ? (changes) => {
                          const hb = feature as HomeButtonFeature
                          setPreviewHomeButton(feature.id, hb, changes)
                        }
                      : undefined
                  }
                  onHomeButtonCommit={
                    feature.type === 'homeButton'
                      ? (changes) => {
                          clearPreviewHomeButton()
                          handleUpdateFeature(feature.id, changes)
                        }
                      : undefined
                  }
                  onDecalPreview={
                    feature.type === 'cameraLens' ||
                    feature.type === 'displayCutout' ||
                    feature.type === 'homeIndicator'
                      ? (changes) => {
                          const f = feature as CameraLensFeature | DisplayCutoutFeature | HomeIndicatorFeature
                          const base: PreviewDecalFeature =
                            f.type === 'cameraLens'
                              ? {type: 'cameraLens', xOffset: f.xOffset, yOffset: f.yOffset, radius: f.radius}
                              : f.type === 'displayCutout'
                                ? {
                                    type: 'displayCutout',
                                    xOffset: f.xOffset,
                                    yOffset: f.yOffset,
                                    width: f.width,
                                    height: f.height,
                                    cornerRadius: f.cornerRadius,
                                  }
                                : {
                                    type: 'homeIndicator',
                                    yOffset: f.yOffset,
                                    width: f.width,
                                    height: f.height,
                                    cornerRadius: f.cornerRadius,
                                    dark: f.dark,
                                  }
                          setPreviewDecalFeature(feature.id, base, changes)
                        }
                      : undefined
                  }
                  onDecalCommit={
                    feature.type === 'cameraLens' ||
                    feature.type === 'displayCutout' ||
                    feature.type === 'homeIndicator'
                      ? (changes) => {
                          clearPreviewDecalFeature()
                          handleUpdateFeature(feature.id, changes)
                        }
                      : undefined
                  }
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}

        {/* Custom decals — rendered as feature accordions alongside the other features */}
        {frontDecals.map((decal) => (
          <Box key={decal.id} sx={sx.groupCard}>
            <Accordion disableGutters square defaultExpanded={decal.id === newlyAddedId} sx={sx.featureAccordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.featureExpandIcon} />} sx={sx.featureSummary}>
                <Typography sx={sx.groupLabel}>{t('device.frontFace.featureTypes.customDecal')}</Typography>
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={(e: ReactMouseEvent) => {
                    e.stopPropagation()
                    handleRemoveDecal(decal.id)
                  }}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      handleRemoveDecal(decal.id)
                    }
                  }}
                  sx={sx.deleteButton}
                >
                  <DeleteIcon sx={{fontSize: 16}} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={sx.featureDetails}>
                <CustomDecalItem
                  decal={decal}
                  unit={unit}
                  deviceWidth={deviceWidth}
                  deviceHeight={deviceHeight}
                  deviceDepth={deviceDepth}
                  deviceEdgeBevel={deviceEdgeBevel}
                  deviceCornerRadius={deviceCornerRadius}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}

        {/* Add feature button */}
        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleOpenMenu}
          sx={sx.addButton}
        >
          {t('device.frontFace.addFeature')}
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          transformOrigin={{vertical: 'top', horizontal: 'center'}}
        >
          {FEATURE_TYPES.map((featureType) => (
            <MenuItem key={featureType} onClick={() => handleAddFeature(featureType)} sx={sx.menuItem}>
              {t(`device.frontFace.featureTypes.${featureType}`)}
            </MenuItem>
          ))}
          <MenuItem onClick={handleAddDecal} sx={sx.menuItem}>
            {t('device.frontFace.featureTypes.customDecal')}
          </MenuItem>
        </Menu>
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles — outer sub-accordion ──────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '0 4px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '4px',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  groupCard: {
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
  },
  featureAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  featureSummary: {
    minHeight: 32,
    padding: '0 8px',
    '&.Mui-expanded': {minHeight: 32},
    '& .MuiAccordionSummary-content': {
      margin: '4px 0',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.Mui-expanded': {margin: '4px 0'},
    },
  },
  featureExpandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 16,
  },
  featureDetails: {
    padding: '0 8px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: tokens.text.primary,
  },
  deleteButton: {
    color: tokens.text.muted,
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      color: '#ef5350',
    },
  },
  addButton: {
    fontSize: 12,
    textTransform: 'none' as const,
    color: tokens.text.primary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.secondary,
    },
  },
  menuItem: {
    fontSize: 13,
  },
})

export default DeviceFrontFacePanel
